import React, { FC, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import Loading from '../components/system/Loading'
import ErrorComponent from '../components/system/Error'
import VerifiedPlayer from '../verification/VerifiedPlayer'
import { resolvePublicContainer } from '../system/apitools'
import { requestedVersionIndex, deletedVideoIds } from './versionContext'
import useRouter from '../hooks/useRouter'

const PUBLIC_API = '/api/v1/public/videos'

// Same container-key resolution as PublicVideo — see the note there. The one
// difference is what an embed does about a superseded version: nothing
// page-level. An iframe is often barely taller than the control bar, and a
// banner would eat the picture; the versions chip on the badge and the panel
// behind it carry the whole story instead, and both live inside the player.
const EmbedVideoPage: FC = () => {
  const { params, navigate } = useRouter()
  const key = params.key
  const versionIndex = requestedVersionIndex(params.index)
  const [hasStarted, setHasStarted] = useState(false)

  const malformedVersion = params.index !== undefined && versionIndex === undefined

  const { isPending, error, data: resolution } = useQuery({
    queryKey: ['public-container', key, versionIndex],
    queryFn: () => resolvePublicContainer(key, versionIndex),
    enabled: !!key && !malformedVersion,
    // A mistyped slug is a 404, and a 404 does not become a 200 by asking
    // again. React Query's default is three retries with backoff, which left
    // a stranger looking at a spinner for about fifteen seconds before being
    // told the link was wrong. Server errors are still worth retrying.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== undefined && status >= 400 && status < 500) return false
      return failureCount < 3
    },
  })

  const resolvedKind = resolution?.resolved_kind
  const canonicalSlug = resolution?.canonical_slug
  useEffect(() => {
    if (!canonicalSlug || resolvedKind === 'slug') return
    if (versionIndex === undefined) navigate('embed', { key: canonicalSlug })
    else navigate('embed-version', { key: canonicalSlug, index: versionIndex })
  }, [canonicalSlug, resolvedKind, versionIndex, navigate])

  const video = resolution?.video
  const container = resolution?.container

  const { participants, attributions, subtitleOffset } = useMemo(() => {
    if (!video) return { participants: [], attributions: {}, subtitleOffset: 0 }
    return {
      participants: video.participants || [],
      attributions: video.speaker_attributions || {},
      subtitleOffset: video.subtitle_offset || 0,
    }
  }, [video])

  const participantsWithAvatarUrls = useMemo(() => {
    if (!video) return []
    return participants.map((p) => ({
      ...p,
      avatar_url: p.avatar_url
        ? `${PUBLIC_API}/${video.id}/participants/${p.id}/avatar/image?v=${encodeURIComponent(p.avatar_url)}`
        : undefined,
    }))
  }, [participants, video])

  if (!key) return <ErrorComponent message="Video not found" />
  if (malformedVersion) return <ErrorComponent message="Version not found" />
  if (isPending) return <Loading />
  if (error) return <ErrorComponent message={error.message} />
  if (!video || !container) return <ErrorComponent message="Video not found" />

  if (video.state !== 'ready' || !video.stream_url) {
    return <ErrorComponent message="Video not available" />
  }

  const subtitleContentUrl = video.subtitles
    ? `${PUBLIC_API}/${video.id}/subtitles/content`
    : undefined

  const thumbnailUrl = video.thumbnail_url
    ? `${PUBLIC_API}/${video.id}/thumbnail/image?v=${encodeURIComponent(video.thumbnail_url)}`
    : undefined

  // Show poster with play button before user starts playback
  if (!hasStarted && thumbnailUrl) {
    return (
      <Box
        onClick={() => setHasStarted(true)}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: '#000',
          backgroundImage: `url(${thumbnailUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            // Page-level element (not inside the player box), so vmin is a
            // fair proxy for "how big is the embed" here.
            width: 'clamp(48px, 12vmin, 80px)',
            height: 'clamp(48px, 12vmin, 80px)',
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s ease, background-color 0.2s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              backgroundColor: 'rgba(0,0,0,0.8)',
            },
          }}
        >
          <PlayArrowIcon sx={{ color: '#fff', fontSize: 'clamp(28px, 7vmin, 48px)' }} />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, backgroundColor: '#000' }}>
      {/* Same wiring as PublicVideo. There is no embed-specific form any
          more: the badge and panel that had one were deleted in T14 of
          docs/plans/2026-09-07-verification-overlay.md, and the glyph and
          panel that replaced them size themselves to the player. */}
      <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <VerifiedPlayer
          video={video}
          versions={{
            containerId: container.id,
            containerAuthority: container.chain?.authority,
            deletedVideoIds: deletedVideoIds(container),
            versionList: container.versions,
            // An embed lives in someone else's page; sending a viewer to the
            // full public page is the honest destination for "watch the
            // current version", and it is the only address this iframe knows
            // that isn't itself.
            currentHref: canonicalSlug ? `/video/${canonicalSlug}` : undefined,
          }}
          player={{
            src: video.stream_url,
            type: 'application/x-mpegURL',
            // The programme title, top-left in the player. `name` is only the
            // fallback line, for a video saved before series and episode
            // existed; with all three empty the overlay renders nothing and
            // the player looks exactly as it did.
            series: video.series,
            episode: video.episode,
            name: video.name,
            participants: participantsWithAvatarUrls,
            subtitleUrl: subtitleContentUrl,
            subtitleOffset,
            speakerAttributions: attributions,
            rotationAdjustment: video.rotation_adjustment,
            fourView: video.four_view,
            startTilt: video.start_tilt,
            fillHeight: true,
            autoPlay: hasStarted,
            masterLanguage: video.subtitles?.master_language,
          }}
        />
      </Box>
    </Box>
  )
}

export default EmbedVideoPage
